import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Navbar from '../components/Navbar';
import CalendarView from '../components/CalendarView';
import ListView from '../components/ListView';
import OpportunityFilters from '../components/OpportunityFilters';
import { Calendar, List } from 'lucide-react';
import { toast } from 'sonner';

const Home = () => {
  const { user } = useContext(AuthContext);
  const [opportunities, setOpportunities] = useState([]);
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);
  const [calendarOpportunities, setCalendarOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('calendar');

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      // List view: only upcoming (default). Calendar view: include past so the
      // calendar still shows historical activity (read-only view).
      const [upcomingRes, allRes] = await Promise.all([
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/opportunities?view=all`),
      ]);
      setOpportunities(upcomingRes.data);
      setFilteredOpportunities(upcomingRes.data);
      setCalendarOpportunities(allRes.data);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = useCallback(async (filters) => {
    // Build query params
    const params = new URLSearchParams();
    
    if (filters.day_of_week.length > 0) {
      params.append('day_of_week', filters.day_of_week.join(','));
    }
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.town) {
      params.append('town', filters.town);
    }
    if (filters.categories.length > 0) {
      params.append('categories', filters.categories.join(','));
    }
    if (filters.duration_bucket) {
      params.append('duration_bucket', filters.duration_bucket);
    }

    try {
      const queryString = params.toString();
      // List view honors filters & defaults to upcoming. Calendar view also
      // honors filters but includes past opportunities (read-only context).
      const listUrl = queryString ? `${API}/opportunities/filter?${queryString}` : `${API}/opportunities`;
      const calParams = new URLSearchParams(params);
      calParams.set('view', 'all');
      const calUrl = `${API}/opportunities/filter?${calParams.toString()}`;
      const [listRes, calRes] = await Promise.all([
        axios.get(listUrl),
        axios.get(calUrl),
      ]);
      setFilteredOpportunities(listRes.data);
      setCalendarOpportunities(calRes.data);
    } catch (error) {
      toast.error('Failed to filter opportunities');
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="text-lg">Loading opportunities...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">Volunteer Opportunities</h1>
          <p className="text-base text-[#666666]">
            Find and sign up for volunteer opportunities in Ossipee and Effingham.
          </p>
        </div>

        <OpportunityFilters onFilterChange={handleFilterChange} />

        <div className="flex gap-2 mb-6">
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${
              view === 'calendar'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setView('calendar')}
            data-testid="calendar-view-button"
          >
            <Calendar className="w-4 h-4" />
            Calendar
          </button>
          <button
            className={`px-4 py-2 rounded-lg flex items-center gap-2 font-medium transition-colors ${
              view === 'list'
                ? 'bg-[#8B2E3C] text-white'
                : 'bg-white text-[#222222] border-2 border-[#E9DDCF] hover:border-[#8B2E3C]'
            }`}
            onClick={() => setView('list')}
            data-testid="list-view-button"
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>

        {view === 'calendar' ? (
          <CalendarView opportunities={calendarOpportunities} onRefresh={fetchOpportunities} />
        ) : (
          <ListView opportunities={filteredOpportunities} onRefresh={fetchOpportunities} />
        )}
      </div>
    </div>
  );
};

export default Home;